/**
 * Command palette / search modal (Ctrl+K).
 *
 * Searches across 6 categories:
 * - Events (by title, filtered by current time range)
 * - Countries (from country-centroids config)
 * - Hotspots (focal points + convergence zones from analysis)
 * - Infrastructure (247 infrastructure graph nodes)
 * - Signals (aggregated intelligence signals)
 * - Panels (all registered panels, open on select)
 *
 * Features:
 * - Fuzzy substring matching
 * - Keyboard navigation (Arrow Up/Down, Enter, Escape)
 * - Recent searches stored in localStorage
 * - Category badges on results
 * - Map fly-to on geographic results
 */

import { appStore } from '@/core/store';
import { eventBus } from '@/core/events';
import { panelManager } from '@/panels/panel-manager';
import { getMap } from '@/map/engine';
import { filterEventsByTimeRange } from '@/utils/time-filter';
import { getAllCountryNames, getCountryCentroid, getCountryCodeFromName } from '@/config/country-centroids';
import { INFRA_NODES } from '@/config/infrastructure-graph';
import { PANEL_REGISTRY } from '@/config/panels';
import { getAnalysisEngine } from '@/intelligence/analysis/engine';

const MAX_RESULTS = 30;
const MAX_PER_CATEGORY = 8;
const MAX_RECENT = 5;
const LS_RECENT_KEY = 'sentinel-recent-searches';

interface SearchResult {
  id: string;
  label: string;
  category: string;
  categoryColor: string;
  sublabel?: string;
  action: () => void;
}

const CATEGORY_COLORS: Record<string, string> = {
  'Event': 'var(--accent-blue)',
  'Country': 'var(--accent-green)',
  'Hotspot': 'var(--accent-red)',
  'Infrastructure': 'var(--accent-amber)',
  'Signal': 'var(--severity-high)',
  'Panel': 'var(--text-secondary)',
};

export class SearchModal {
  private overlay: HTMLElement;
  private input: HTMLInputElement;
  private resultsList: HTMLElement;
  private recentSection: HTMLElement;
  private results: SearchResult[] = [];
  private activeIndex = -1;
  private visible = false;
  private cleanupFns: (() => void)[] = [];

  constructor() {
    // Backdrop
    this.overlay = document.createElement('div');
    this.overlay.className = 'search-modal-overlay';
    this.overlay.style.cssText = `
      position: fixed; inset: 0; z-index: 10000;
      background: rgba(0, 0, 0, 0.6);
      display: none; align-items: flex-start; justify-content: center;
      padding-top: 15vh;
    `;
    this.overlay.onclick = (e) => {
      if (e.target === this.overlay) this.hide();
    };

    // Modal container
    const modal = document.createElement('div');
    modal.className = 'search-modal';
    modal.style.cssText = `
      width: 560px; max-width: 90vw; max-height: 60vh;
      background: var(--bg-primary); border: 1px solid var(--border-primary);
      border-radius: 12px; box-shadow: 0 16px 48px rgba(0,0,0,0.5);
      display: flex; flex-direction: column; overflow: hidden;
    `;

    // Input area
    const inputWrap = document.createElement('div');
    inputWrap.style.cssText = `
      display: flex; align-items: center; padding: 12px 16px; gap: 10px;
      border-bottom: 1px solid var(--border-primary);
    `;

    const searchIcon = document.createElement('span');
    searchIcon.style.cssText = 'color: var(--text-muted); font-size: 16px; flex-shrink: 0;';
    searchIcon.textContent = '\uD83D\uDD0D';
    inputWrap.appendChild(searchIcon);

    this.input = document.createElement('input');
    this.input.type = 'text';
    this.input.placeholder = 'Search events, countries, panels...';
    this.input.style.cssText = `
      flex: 1; background: none; border: none; outline: none;
      color: var(--text-primary); font-size: 15px;
      font-family: var(--font-sans);
    `;
    this.input.oninput = () => this.onInput();
    this.input.onkeydown = (e) => this.onKeydown(e);
    inputWrap.appendChild(this.input);

    const kbd = document.createElement('kbd');
    kbd.style.cssText = `
      font-size: 10px; color: var(--text-muted); background: var(--bg-secondary);
      padding: 2px 6px; border-radius: 3px; border: 1px solid var(--border-primary);
    `;
    kbd.textContent = 'ESC';
    inputWrap.appendChild(kbd);

    modal.appendChild(inputWrap);

    // Results area
    const resultsWrap = document.createElement('div');
    resultsWrap.style.cssText = 'flex: 1; overflow-y: auto; padding: 8px 0;';

    this.recentSection = document.createElement('div');
    this.recentSection.style.cssText = 'padding: 0 8px;';
    resultsWrap.appendChild(this.recentSection);

    this.resultsList = document.createElement('div');
    this.resultsList.style.cssText = 'padding: 0 8px;';
    resultsWrap.appendChild(this.resultsList);

    modal.appendChild(resultsWrap);

    // Footer
    const footer = document.createElement('div');
    footer.style.cssText = `
      padding: 8px 16px; border-top: 1px solid var(--border-primary);
      font-size: 10px; color: var(--text-muted);
      display: flex; gap: 16px;
    `;
    footer.textContent = '\u2191\u2193 Navigate  \u23CE Select  Esc Close';
    modal.appendChild(footer);

    this.overlay.appendChild(modal);

    // Listen for open event
    this.cleanupFns.push(
      eventBus.on('panel:toggled', ({ panelId, visible }) => {
        if (panelId === 'search' && visible) this.show();
      }),
    );
  }

  getElement(): HTMLElement {
    return this.overlay;
  }

  destroy(): void {
    for (const fn of this.cleanupFns) fn();
    this.cleanupFns = [];
    this.overlay.remove();
  }

  show(): void {
    if (this.visible) return;
    this.visible = true;
    this.overlay.style.display = 'flex';
    this.input.value = '';
    this.results = [];
    this.activeIndex = -1;
    this.renderRecent();
    this.resultsList.textContent = '';
    // Focus after display renders
    requestAnimationFrame(() => this.input.focus());
  }

  hide(): void {
    if (!this.visible) return;
    this.visible = false;
    this.overlay.style.display = 'none';
    this.input.value = '';
  }

  private onInput(): void {
    const query = this.input.value.trim().toLowerCase();
    if (query.length === 0) {
      this.results = [];
      this.resultsList.textContent = '';
      this.recentSection.style.display = '';
      this.activeIndex = -1;
      return;
    }

    this.recentSection.style.display = 'none';
    this.results = this.search(query);
    this.activeIndex = this.results.length > 0 ? 0 : -1;
    this.renderResults();
  }

  private onKeydown(e: KeyboardEvent): void {
    if (e.key === 'Escape') {
      e.preventDefault();
      this.hide();
      return;
    }

    if (e.key === 'ArrowDown') {
      e.preventDefault();
      if (this.results.length > 0) {
        this.activeIndex = (this.activeIndex + 1) % this.results.length;
        this.renderResults();
      }
      return;
    }

    if (e.key === 'ArrowUp') {
      e.preventDefault();
      if (this.results.length > 0) {
        this.activeIndex = (this.activeIndex - 1 + this.results.length) % this.results.length;
        this.renderResults();
      }
      return;
    }

    if (e.key === 'Enter') {
      e.preventDefault();
      if (this.activeIndex >= 0 && this.activeIndex < this.results.length) {
        const result = this.results[this.activeIndex];
        this.saveRecent(result.label);
        result.action();
        this.hide();
      }
      return;
    }
  }

  /** Run search across all categories. */
  private search(query: string): SearchResult[] {
    const results: SearchResult[] = [];

    // Panels
    this.searchPanels(query, results);
    // Countries
    this.searchCountries(query, results);
    // Events
    this.searchEvents(query, results);
    // Infrastructure
    this.searchInfrastructure(query, results);
    // Hotspots / signals from analysis
    this.searchAnalysis(query, results);

    return results.slice(0, MAX_RESULTS);
  }

  private searchPanels(query: string, results: SearchResult[]): void {
    let count = 0;
    for (const panel of PANEL_REGISTRY) {
      if (count >= MAX_PER_CATEGORY) break;
      if (
        panel.name.toLowerCase().includes(query) ||
        panel.description.toLowerCase().includes(query)
      ) {
        results.push({
          id: `panel-${panel.id}`,
          label: panel.name,
          category: 'Panel',
          categoryColor: CATEGORY_COLORS['Panel'],
          sublabel: panel.description,
          action: () => panelManager.showPanel(panel.id),
        });
        count++;
      }
    }
  }

  private searchCountries(query: string, results: SearchResult[]): void {
    const names = getAllCountryNames();
    let count = 0;
    for (const name of names) {
      if (count >= MAX_PER_CATEGORY) break;
      if (name.includes(query)) {
        const displayName = name.replace(/\b\w/g, c => c.toUpperCase());
        const code = getCountryCodeFromName(name);
        results.push({
          id: `country-${name}`,
          label: displayName,
          category: 'Country',
          categoryColor: CATEGORY_COLORS['Country'],
          sublabel: code ? `ISO ${code}` : undefined,
          action: () => {
            const centroid = getCountryCentroid(name);
            if (centroid) {
              const map = getMap();
              if (map) {
                // centroid is [lat, lng], flyTo needs [lng, lat]
                map.flyTo({ center: [centroid[1], centroid[0]], zoom: 5, duration: 800 });
              }
              if (code) {
                eventBus.emit('country:selected', { name: displayName, code });
              }
            }
          },
        });
        count++;
      }
    }
  }

  private searchEvents(query: string, results: SearchResult[]): void {
    const state = appStore.getState();
    const events = filterEventsByTimeRange(state.events, state.timeRange);
    let count = 0;
    for (const ev of events) {
      if (count >= MAX_PER_CATEGORY) break;
      if (ev.title.toLowerCase().includes(query)) {
        results.push({
          id: `event-${ev.id}`,
          label: ev.title,
          category: 'Event',
          categoryColor: CATEGORY_COLORS['Event'],
          sublabel: `${ev.source} \u00B7 ${ev.type}`,
          action: () => {
            eventBus.emit('event:selected', ev);
            if (ev.location) {
              const map = getMap();
              if (map) {
                map.flyTo({
                  center: [ev.location.lng, ev.location.lat],
                  zoom: Math.max(map.getZoom(), 8),
                  duration: 600,
                });
              }
            }
          },
        });
        count++;
      }
    }
  }

  private searchInfrastructure(query: string, results: SearchResult[]): void {
    let count = 0;
    for (const node of INFRA_NODES) {
      if (count >= MAX_PER_CATEGORY) break;
      if (
        node.name.toLowerCase().includes(query) ||
        (node.description?.toLowerCase().includes(query))
      ) {
        results.push({
          id: `infra-${node.id}`,
          label: node.name,
          category: 'Infrastructure',
          categoryColor: CATEGORY_COLORS['Infrastructure'],
          sublabel: `${node.type} \u00B7 ${node.country ?? 'Global'}`,
          action: () => {
            const map = getMap();
            if (map) {
              map.flyTo({
                center: [node.lng, node.lat],
                zoom: Math.max(map.getZoom(), 6),
                duration: 600,
              });
            }
          },
        });
        count++;
      }
    }
  }

  private searchAnalysis(query: string, results: SearchResult[]): void {
    const engine = getAnalysisEngine();
    const lastResult = engine?.getLastResult();
    if (!lastResult) return;

    // Focal points
    let count = 0;
    for (const fp of lastResult.focalPoints) {
      if (count >= 4) break;
      if (fp.entity.toLowerCase().includes(query)) {
        results.push({
          id: `hotspot-${fp.id}`,
          label: fp.entity,
          category: 'Hotspot',
          categoryColor: CATEGORY_COLORS['Hotspot'],
          sublabel: `${fp.urgencyLevel} \u00B7 score ${fp.score}`,
          action: () => panelManager.showPanel('insights'),
        });
        count++;
      }
    }

    // Aggregated signals
    count = 0;
    for (const sig of lastResult.aggregatedSignals) {
      if (count >= 4) break;
      if (sig.country.toLowerCase().includes(query)) {
        results.push({
          id: `signal-${sig.countryCode}`,
          label: `${sig.country} Intelligence Signal`,
          category: 'Signal',
          categoryColor: CATEGORY_COLORS['Signal'],
          sublabel: `${sig.signals.length} signals \u00B7 convergence ${sig.convergenceScore}`,
          action: () => {
            eventBus.emit('signal:inspect', sig);
          },
        });
        count++;
      }
    }
  }

  /** Render search results list. */
  private renderResults(): void {
    this.resultsList.textContent = '';

    if (this.results.length === 0) {
      const empty = document.createElement('div');
      empty.style.cssText = 'padding: 24px 12px; text-align: center; color: var(--text-muted); font-size: 12px;';
      empty.textContent = 'No results found';
      this.resultsList.appendChild(empty);
      return;
    }

    for (let i = 0; i < this.results.length; i++) {
      const r = this.results[i];
      const isActive = i === this.activeIndex;

      const row = document.createElement('div');
      row.style.cssText = `
        display: flex; align-items: center; gap: 10px;
        padding: 8px 12px; border-radius: 6px; cursor: pointer;
        background: ${isActive ? 'var(--bg-secondary)' : 'transparent'};
      `;

      row.onmouseenter = () => {
        this.activeIndex = i;
        this.renderResults();
      };

      row.onclick = () => {
        this.saveRecent(r.label);
        r.action();
        this.hide();
      };

      // Category badge
      const badge = document.createElement('span');
      badge.style.cssText = `
        font-size: 9px; font-weight: 600; text-transform: uppercase;
        padding: 2px 6px; border-radius: 3px;
        background: ${r.categoryColor}22;
        color: ${r.categoryColor};
        flex-shrink: 0;
      `;
      badge.textContent = r.category;
      row.appendChild(badge);

      // Label
      const labelWrap = document.createElement('div');
      labelWrap.style.cssText = 'flex: 1; min-width: 0;';

      const label = document.createElement('div');
      label.style.cssText = `
        font-size: 13px; color: var(--text-primary);
        white-space: nowrap; overflow: hidden; text-overflow: ellipsis;
      `;
      label.textContent = r.label;
      labelWrap.appendChild(label);

      if (r.sublabel) {
        const sub = document.createElement('div');
        sub.style.cssText = `
          font-size: 10px; color: var(--text-muted);
          white-space: nowrap; overflow: hidden; text-overflow: ellipsis;
        `;
        sub.textContent = r.sublabel;
        labelWrap.appendChild(sub);
      }

      row.appendChild(labelWrap);

      // Enter hint for active item
      if (isActive) {
        const hint = document.createElement('span');
        hint.style.cssText = 'font-size: 10px; color: var(--text-muted); flex-shrink: 0;';
        hint.textContent = '\u23CE';
        row.appendChild(hint);
      }

      this.resultsList.appendChild(row);
    }
  }

  /** Render recent searches section. */
  private renderRecent(): void {
    this.recentSection.textContent = '';
    this.recentSection.style.display = '';

    const recent = this.loadRecent();
    if (recent.length === 0) {
      const hint = document.createElement('div');
      hint.style.cssText = 'padding: 24px 12px; text-align: center; color: var(--text-muted); font-size: 12px;';
      hint.textContent = 'Type to search across events, countries, infrastructure, and panels';
      this.recentSection.appendChild(hint);
      return;
    }

    const header = document.createElement('div');
    header.style.cssText = 'font-size: 10px; font-weight: 600; color: var(--text-muted); text-transform: uppercase; padding: 8px 12px 4px; letter-spacing: 0.05em;';
    header.textContent = 'Recent';
    this.recentSection.appendChild(header);

    for (const term of recent) {
      const row = document.createElement('div');
      row.style.cssText = `
        padding: 6px 12px; border-radius: 6px; cursor: pointer;
        font-size: 12px; color: var(--text-secondary);
      `;
      row.textContent = term;
      row.onmouseenter = () => { row.style.background = 'var(--bg-secondary)'; };
      row.onmouseleave = () => { row.style.background = 'transparent'; };
      row.onclick = () => {
        this.input.value = term;
        this.onInput();
      };
      this.recentSection.appendChild(row);
    }
  }

  /** Save a search term to recent searches. */
  private saveRecent(term: string): void {
    try {
      const recent = this.loadRecent().filter(t => t !== term);
      recent.unshift(term);
      localStorage.setItem(LS_RECENT_KEY, JSON.stringify(recent.slice(0, MAX_RECENT)));
    } catch { /* localStorage unavailable */ }
  }

  /** Load recent searches from localStorage. */
  private loadRecent(): string[] {
    try {
      const raw = localStorage.getItem(LS_RECENT_KEY);
      if (!raw) return [];
      const parsed = JSON.parse(raw);
      if (Array.isArray(parsed)) return parsed.slice(0, MAX_RECENT);
    } catch { /* ignore */ }
    return [];
  }
}

/** Factory function to create the search modal and mount it. */
export function createSearchModal(): { element: HTMLElement; modal: SearchModal } {
  const modal = new SearchModal();
  return { element: modal.getElement(), modal };
}
